import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useJitsu } from '@jitsu/jitsu-react';
import { useNavigate } from '@tanstack/react-router';

import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@tg-web/components';
import { CheckmarkIcon, DeleteIcon, HelpIcon } from '@tg-web/icons';
import { useDisclosure } from '@tg-web/utils';

import { OjEvent } from '../../../shared/api';
import { EventsAnalytics } from '../../../shared/model/analytics';
import { IslandSegment, IslandSegmentedCard } from '../../../shared/ui/island';
import { useAttendEventHandler, useMissEventHandler } from '../lib';
import { AllMissedAttendStatuses, AttendStatus } from '../model';
import { OverlapEvents } from './OverlapEvents';

/** The three answers, in the order the design puts them. */
const GOING = 'going';
const NOT_GOING = 'not_going';
const MAYBE = 'maybe';

type EventRsvpCardProps = {
  eventData: OjEvent;
  isNewUser: boolean;
  onChangeAttendStatus: VoidFunction;
  /** Fired once a first Going lands, to move the user on. */
  onFirstAttend: VoidFunction;
};

/**
 * The RSVP triplet, as a card under the share row.
 *
 * Replaces the bar that used to be pinned to the bottom of the viewport. It
 * keeps that bar's two passengers, which were easy to miss because neither is
 * visible until something goes a particular way:
 *
 * - the **clash sheet**, which needs three things to travel together -- the
 *   disclosure, the record of *which* answer was tapped, and the replay that
 *   fires it again once the user confirms.
 * - the **new-user tooltip**, which used to hang off the Yes button. It hangs
 *   off the whole card now: the card is near the top of the page rather than
 *   the bottom, so Radix flips it below, and pointing at one segment of a card
 *   that is itself the subject of the tip was never the point.
 *
 * Dispatch is `useAttendEventHandler` / `useMissEventHandler`, unchanged.
 * Nothing here is optimistic: `value` stays on the old answer until the refetch
 * lands, and the tapped segment shows a spinner in the meantime.
 */
export function EventRsvpCard({
  eventData,
  isNewUser,
  onChangeAttendStatus,
  onFirstAttend,
}: EventRsvpCardProps) {
  const { t } = useTranslation();
  const { analytics } = useJitsu();
  const navigate = useNavigate();
  const overlapModal = useDisclosure();
  const tooltip = useDisclosure({ defaultOpen: isNewUser });

  // Which answer is in flight. Also what the clash sheet replays on confirm --
  // the reason this is state and not a boolean: the sheet is reached from both
  // Going and Maybe, and it has to put back the one that was actually tapped.
  const [pendingStatus, setPendingStatus] = useState<
    AttendStatus.attend | AttendStatus.maybe | undefined
  >(undefined);

  const attendStatus = eventData.attend_status;
  const hasMissedStatus = AllMissedAttendStatuses.includes(attendStatus);

  const [handleMissEvent, missReq] = useMissEventHandler(eventData, () => {
    onChangeAttendStatus();
    if (isNewUser) {
      navigate({ to: '/welcome-event-declined' });
    }
  });

  const [handleAttendEvent, attendReq] = useAttendEventHandler(() => {
    if (hasMissedStatus) {
      onFirstAttend();
    } else {
      onChangeAttendStatus();
    }
  });

  /*
   * The pending segment is released however the attempt ends: it landed, or
   * it failed for good. Keyed on both, so a second failure after a first
   * (error, then pending, then error again) releases it again.
   */
  useEffect(() => {
    if (attendReq.isSuccess || attendReq.isError) {
      setPendingStatus(undefined);
    }
  }, [attendReq.isSuccess, attendReq.isError]);

  // The third way an attempt ends: it was never sent, because the recurrence
  // question was dismissed. The handler says so rather than leave a spinner.
  const attend = async (status: AttendStatus.attend | AttendStatus.maybe) => {
    const sent = await handleAttendEvent(eventData, status);

    if (!sent) {
      setPendingStatus(undefined);
    }
  };

  const dispatch = (status: AttendStatus.attend | AttendStatus.maybe) => {
    setPendingStatus(status);
    // A clash is only worth raising for somebody who is not already committed;
    // moving between Going and Maybe cannot introduce one they have not already
    // accepted.
    if (hasMissedStatus) {
      overlapModal.onOpen();
    } else {
      attend(status);
    }
  };

  const currentAnswer = ANSWER_BY_STATUS[attendStatus];

  const handleSelect = (key: string) => {
    /*
     * The answer already given is not a new one. Tapping it does nothing --
     * no prompt, no request, no analytics, no navigation -- as the bar this
     * replaces did by disabling its current button.
     */
    if (key === currentAnswer) {
      return;
    }

    tooltip.onClose();

    if (key === GOING) {
      analytics.track(EventsAnalytics.JOIN, { eventId: eventData.id });
      dispatch(AttendStatus.attend);
      return;
    }

    if (key === MAYBE) {
      analytics.track(EventsAnalytics.MAYBE, { eventId: eventData.id });
      dispatch(AttendStatus.maybe);
      return;
    }

    analytics.track(EventsAnalytics.NOT_INTERESTED, { eventId: eventData.id });
    handleMissEvent();
  };

  /*
   * Each answer has its own colour -- the same green and yellow the
   * participant list's status dots use, and the destructive red -- so the card
   * reads before the labels do. All 24px boxes: the three glyphs are drawn at
   * different scales in their own viewBoxes, and 24 is what brings each to the
   * size the design gives it.
   */
  const segments: IslandSegment[] = [
    {
      icon: <CheckmarkIcon height={24} width={24} />,
      iconClassName: 'text-tg-custom-green',
      key: GOING,
      label: t('all:view_event.stack_event_owner.going'),
      pending: pendingStatus === AttendStatus.attend,
    },
    {
      icon: <DeleteIcon height={24} width={24} />,
      iconClassName: 'text-tg-destructive-text',
      key: NOT_GOING,
      label: t('all:view_event.stack_event_owner.not_going'),
      pending: missReq.isPending,
    },
    {
      icon: <HelpIcon height={24} width={24} />,
      iconClassName: 'text-tg-custom-yellow',
      key: MAYBE,
      label: t('all:view_event.stack_event_owner.maybe'),
      pending: pendingStatus === AttendStatus.maybe,
    },
  ];

  return (
    <>
      <Tooltip open={tooltip.open}>
        {/*
         * A plain div, not the card: `asChild` hands the trigger a ref, and the
         * card is a function component without one. The div is the card's own
         * box either way.
         */}
        <TooltipTrigger asChild>
          <div>
            <IslandSegmentedCard
              onSelect={handleSelect}
              segments={segments}
              value={currentAnswer}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-64"
          collisionPadding={24}
          onPointerDownOutside={tooltip.onClose}
        >
          <TooltipArrow />
          {t('all:event.status_panel.tooltip')}
        </TooltipContent>
      </Tooltip>
      <OverlapEvents
        onCancel={() => {
          setPendingStatus(undefined);
          overlapModal.onClose();
        }}
        onContinue={() => {
          overlapModal.onClose();
          if (pendingStatus !== undefined) {
            attend(pendingStatus);
          }
        }}
        duration={eventData.duration.toString()}
        enabled={overlapModal.open}
        start_at={eventData.start_at}
      />
    </>
  );
}

/**
 * Only the three answers a person can give map to a segment. `notAttended` and
 * `removed` are the absence of an answer, and resolve to nothing -- which is
 * what puts the card in its unpicked state.
 */
const ANSWER_BY_STATUS: Record<number, string | undefined> = {
  [AttendStatus.attend]: GOING,
  [AttendStatus.maybe]: MAYBE,
  [AttendStatus.missed]: NOT_GOING,
};
